import React from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import ConflictIcon from '@/assets/icons/conflict';
import LockIcon from '@/assets/icons/lock-lg';
import RemoveIcon from '@/assets/icons/remove-sm';
import { LineSeparator } from '@/components/LineSeparator';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { useIsBetSlipSubmitting, useIsOptionSuspended } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useBetSlipMarketName } from '@/feature/betslip-sbk/hooks/use-bet-slip-market-name';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { hasOddsChanged } from '@/feature/betslip-sbk/utils/betslip-utils';
import { useSplitText } from '@/hooks/use-split-text';

import { AnimatedOddsChangeIcon } from '../AnimatedOddsChangeIcon';
import { OptionName } from '../OptionName';

type ParlaySelectionProps = {
    selectionId: string;
};

const DISABLED_COLOR = 'neutral400' as const;

export const ParlaySelection = ({ selectionId }: ParlaySelectionProps) => {
    const removeSelection = useSbkBetSlipStore(state => state.actions.removeSelection);
    const selection = useSbkBetSlipStore(state => state.selections[selectionId]);
    const isBetSlipSubmitting = useIsBetSlipSubmitting();
    const isSgpFetching = useSbkBetSlipStore(state => state.isSgpFetching);

    const { optionId, eventId, marketId, isComboEnabled } = selection;
    const isDisabled = !isComboEnabled;
    const isSuspended = useIsOptionSuspended(optionId);

    const marketName = useBetSlipMarketName({ marketId, eventId });

    const isGreyedOut = isSuspended || isDisabled;
    const textColor = isGreyedOut ? DISABLED_COLOR : ds.colorToken.text.primary;
    const secondaryTextColor = isGreyedOut ? DISABLED_COLOR : 'neutral300';

    const { Line1, Line2 } = useSplitText({ text: marketName, color: secondaryTextColor });

    const handleRemove = () => {
        GASbkEvents.removeSelection();
        removeSelection(selectionId);
    };

    return (
        <Row>
            <Box mt="s4">
                <TouchableOpacity hitSlop={8} onPress={handleRemove} disabled={isBetSlipSubmitting || isSgpFetching}>
                    <RemoveIcon height={16} width={16} />
                </TouchableOpacity>
            </Box>

            <Box flex={1} ml="s12">
                <Row alignItems="center">
                    <OptionName optionId={optionId} variant="titleMedium" color={textColor} />
                    <Box mx="s8">
                        <LineSeparator vertical style={styles.separator} />
                    </Box>
                    {Line1}
                    <ParlaySelectionIcon selectionId={selectionId} />
                </Row>
                {Line2}
            </Box>
        </Row>
    );
};

const ParlaySelectionIcon = ({ selectionId }: { selectionId: string }) => {
    const optionId = useSbkBetSlipStore(state => state.selections[selectionId]?.optionId);
    const currentOdds = useSbkBetSlipStore(state => state.options[optionId]?.odds);
    const previousOdds = useSbkBetSlipStore(state => state.oddsChanges[optionId]);
    const isComboEnabled = useSbkBetSlipStore(state => state.selections[selectionId]?.isComboEnabled);
    const isSuspended = useIsOptionSuspended(optionId);
    const isDisabled = !isComboEnabled;
    const oddsChanged = hasOddsChanged(currentOdds, previousOdds);

    if (!isSuspended && !isDisabled && !oddsChanged) {
        return null;
    }

    return (
        <Box ml="s12">
            {isSuspended ? (
                <LockIcon />
            ) : isDisabled ? (
                <ConflictIcon />
            ) : (
                <AnimatedOddsChangeIcon
                    hasOddsChanged={oddsChanged}
                    odds={currentOdds}
                    previousOdds={previousOdds ?? null}
                />
            )}
        </Box>
    );
};

const styles = StyleSheet.create({
    separator: {
        height: 16,
    },
});
